import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Link
} from '@mui/material';
import { ShoppingCart, Link as LinkIcon, CheckCircle } from '@mui/icons-material';
import api from '../../../../../utils/api';

const steps = ['Enter Store URL', 'Authorize', 'Complete'];

const ConnectionSetup = ({ onConnectionCreated }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [shopDomain, setShopDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [installUrl, setInstallUrl] = useState('');

  const handleStartInstall = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate shop domain
      let domain = shopDomain.trim();
      if (!domain.includes('.myshopify.com')) {
        domain = `${domain}.myshopify.com`;
      }

      const response = await api.get('/shopify/auth/install', {
        params: { shop: domain }
      });

      if (response.data.success) {
        setInstallUrl(response.data.data.installUrl);
        setActiveStep(1);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start installation');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = () => {
    if (installUrl) {
      window.open(installUrl, '_blank');
      setActiveStep(2);
    }
  };

  const handleComplete = () => {
    if (onConnectionCreated) {
      onConnectionCreated();
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ShoppingCart sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" gutterBottom>
                Connect Your Shopify Store
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sync your Shopify products, inventory, and orders with DymenStock
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Enter your Shopify store URL to begin the connection process.
              </Typography>
              <TextField
                fullWidth
                label="Store URL"
                placeholder="your-store.myshopify.com"
                value={shopDomain}
                onChange={(e) => setShopDomain(e.target.value)}
                helperText="Enter your store name or full myshopify.com URL"
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: '.myshopify.com'
                }}
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleStartInstall}
                disabled={!shopDomain || loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
                fullWidth
              >
                {loading ? 'Connecting...' : 'Connect Store'}
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  You will be redirected to Shopify to authorize DymenStock to access your store data.
                </Typography>
              </Alert>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Click the button below to open Shopify and authorize the connection.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleAuthorize}
                startIcon={<LinkIcon />}
                fullWidth
              >
                Authorize on Shopify
              </Button>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Authorization Complete!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your Shopify store has been successfully connected to DymenStock.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleComplete}
                fullWidth
              >
                Go to Connections
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Required Permissions:</strong> DymenStock will request access to read and write products, 
              inventory, orders, and locations. This allows bidirectional synchronization between your systems.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConnectionSetup;
